import type { PropsWithChildren } from 'react'
import type { LinkProps } from 'react-router-dom'

import type {
  FlexContainerProperties,
  GridContainerProperties,
  ResponsiveStyleValue
} from '../../theme-provider/types'
import type { BreakpointSize } from '../../themes/theme-keys/breakpoints'
import type { PaneProps } from '../_layouts/pane/types'
/* eslint-disable @typescript-eslint/no-explicit-any */
import type Box from '../_primitives/box/box'
import type { BoxProps } from '../_primitives/box/types'
import type { SizeProps } from '../_primitives/types'
import type { EventObserver } from '../_utils'
import type { PopoverProps } from '../_utils/popover/types'
import type Button from '../button/button'
import type { ButtonProps } from '../button/types'

export const TAB_THEME_KEY = 'tabs'

export type SelectionChangedEvent = {
  value: number | string
  label?: string
  icon?: ButtonProps['iconBefore']
  source: 'tabs' | 'panels'
}

export type TabsContext = {
  contextId?: string
  variant?: string
  mode?: 'stack' | 'switch'
  initialSelection?: number | string
  selection?: number | string
  onChange?: (value: number | string) => void
  selectionChangedObserver?: EventObserver<
    (event: SelectionChangedEvent) => void,
    SelectionChangedEvent
  >
}

export type TabIndicatorProps = BoxProps & {
  /**
   * The tabs orientation (layout flow direction).
   */
  orientation: 'horizontal' | 'vertical'
  indicatorPosition?: 'left' | 'right' | 'top' | 'bottom'
  disableAnimation?: boolean
}

export type TabScrollButtonProps = ButtonProps & {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Which direction should the button indicate?
   */
  direction: 'left' | 'right'
  /**
   * The tabs orientation (layout flow direction).
   */
  orientation: 'horizontal' | 'vertical'
  /**
   * enable transition button position
   */
  enableTransition: boolean
}

export type TabsActions = {
  updateIndicator(): void
  updateScrollButtons(): void
}
type TabsType = {
  /**
   * Callback fired when the component mounts.
   * This is useful when you want to trigger an action programmatically.
   * It supports two actions: `updateIndicator()` and `updateScrollButtons()`
   *
   * @param {object} actions This object contains all possible actions
   * that can be triggered programmatically.
   */
  action?: React.Ref<TabsActions>
  /**
   * The label for the Tabs as a string.
   */
  'aria-label'?: string
  /**
   * An id or list of ids separated by a space that label the Tabs.
   */
  'aria-labelledby'?: string
  /**
   * If `true`, the tabs will be centered.
   * This property is intended for large views.
   */
  centered?: boolean
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Determines the color of the indicator.
   */
  indicatorColor?: 'secondary' | 'primary'
  /**
   * Callback fired when the value changes.
   *
   * @param {object} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param { value: React.ReactText, label?: string } source value default to the index of the child (number), label: tab label
   */
  onChange?: (
    event: React.SyntheticEvent,
    source: {
      value: React.ReactText
      label?: string
    }
  ) => void
  /**
   * The tabs orientation (layout flow direction).
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * The component used to render the scroll buttons.
   */
  ScrollButtonComponent?: React.ElementType
  /**
   * Determine behavior of scroll buttons when tabs are set to scroll:
   *
   * - `auto` will only present them when not all the items are visible.
   * - `desktop` will only present them on medium and larger viewports.
   * - `on` will always present them.
   * - `off` will never present them.
   */
  scrollButtons?: 'auto' | 'desktop' | 'on' | 'off'
  /**
   * Breakpoint size to re-hide the scroll button assuming  touch base scrolling is a better experience
   * If not set the feature of re-hiding the button will not be enabled.
   */
  scrollButtonsBreakpoints?: number | BreakpointSize
  /**
   * If `true` the selected tab changes on focus. Otherwise it only
   * changes on activation.
   */
  selectionFollowsFocus?: boolean
  /**
   * Props applied to the tab indicator element.
   */
  TabIndicatorProps?: Partial<
    React.HTMLAttributes<HTMLDivElement> & TabIndicatorProps
  >
  /**
   * Disable Tab Indictator Animation
   */
  disableAnimation?: boolean
  /**
   * Props applied to the [`TabScrollButton`](/api/tab-scroll-button/) element.
   */
  TabScrollButtonProps?: Partial<TabScrollButtonProps>
  /**
   * Determines the color of the `Tab`.
   */
  textColor?: 'secondary' | 'primary' | 'inherit'
  /**
   * The value of the currently selected `Tab`.
   * If you don't want any selected `Tab`, you can set this property to `false`.
   */
  value?: number | string
  /**
   *  Determines additional display behavior of the tabs:
   *
   *  - `scrollable` will invoke scrolling properties and allow for horizontally
   *  scrolling (or swiping) of the tab bar.
   *  -`fullWidth` will make the tabs grow to use all the available space,
   *  which should be used for small views, like on mobile.
   *  - `standard` will render the default state.
   */
  mode?: 'standard' | 'scrollable' | 'fullWidth' | 'more'
  direction?: 'rtl' | 'ltr'
  spacing?: SizeProps
  hasDivider?: boolean
  divider?: React.ReactNode
  indicatorPosition?: 'left' | 'right' | 'top' | 'bottom'
  indicatorType?: 'bar' | 'arrow'
  moreLabel?: string
  morePaneProps?: PaneProps
  moreButtonList?: boolean
  /**
   * use space-between for placement of more button
   */
  spaceBetweenMore?: boolean
  animatedButtons?: boolean
  tabsWrapperProps?: Omit<PaneProps, 'children'>
  /**
   * lenght by percentage of the width or height of the tab, default is 1 = 100%
   */
  indicatorLength?: number
  /**
   * set to true to hide indicator
   */
  hideIndicator?: boolean
}
export type TabsTypeMap<P = {}, D extends React.ElementType = typeof Box> = {
  props: Omit<P, keyof TabsType> & Omit<BoxProps, keyof TabsType> & TabsType
  defaultComponent: D
}
type TabType = {
  /**
   * You can provide your own value. Otherwise, we fallback to the child position index.
   */
  value?: number | string
  /**
   * tab label extracted from child node of the tab or supplied through props
   */
  label?: string
  /**
   * Use to apply selected styling.
   */
  selected?: boolean
  /**
   * Callback fired when the value changes.
   *
   * @param {object} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param { value: React.ReactText, label?: string } source value default to the index of the child (number), label: tab label
   */
  onChange?: (
    event: React.SyntheticEvent,
    source: {
      value: React.ReactText
      label?: string
      icon?: ButtonProps['iconBefore']
    }
  ) => void
  /**
   * If `true` the selected tab changes on focus. Otherwise it only
   * changes on activation.
   */
  selectionFollowsFocus?: boolean
  /**
   * The tabs orientation (layout flow direction).
   */
  orientation?: 'horizontal' | 'vertical'
  indicatorPosition?: 'left' | 'right' | 'top' | 'bottom'
}
export type TabTypeMap<P = {}, D extends React.ElementType = typeof Button> = {
  props: Omit<P, keyof TabType> & Omit<ButtonProps, keyof TabType> & TabType
  defaultComponent: D
}

export type TabProps = Omit<
  TabTypeMap['props'],
  'selectionFollowsFocus' | 'onChange'
>

export type TabPanelProps = BoxProps &
  FlexContainerProperties & {
    value?: number | string
    children?: React.ReactNode
    scrollOffset?: string
    threshold?: number
  }

export type GridTabPanelProps = TabPanelProps &
  GridContainerProperties & {
    columns?: ResponsiveStyleValue<string | number>
  }

export type TabLinkProps = TabProps & {
  to?: LinkProps['to']
}

export type MoreTabProps = BoxProps & {
  includeValues: {
    value: string | number
    label: string
    icon?: ButtonProps['iconBefore']
    placeholder?: string
  }[]
  paneProps?: PaneProps
  popoverProps?: PopoverProps
  buttonList?: boolean
}

export type ScrollIntoViewOptions = {
  behavior?: 'auto' | 'smooth'
  block?: 'start' | 'center' | 'end' | 'nearest'
  inline?: 'start' | 'center' | 'end' | 'nearest'
}

export type TabsContextProviderProps = Omit<
  TabsContext,
  'selectionChangedObserver'
> &
  PropsWithChildren<{ scrollIntoViewOptions?: boolean | ScrollIntoViewOptions }>
